import React from 'react';
import { func, instanceOf, node } from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import { FaroErrorBoundary } from '@grafana/faro-react';
import ServerErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/ServerErrorPage';
import NetworkErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/NetworkErrorPage';
import CodeErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/CodeErrorPage';
import GraphQLErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/GraphQLErrorPage';
import AccessDeniedPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/AccessDeniedPage';
import MaintenanceErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/MaintenanceErrorPage';
import OfflineErrorPage from '@ApplicationWide/ErrorPages/ErrorBoundaryPages/OfflineErrorPage';
import BadNavigationPage from './ErrorPages/BadNavigationPage/BadNavigationPage';
import NavigationError from './NavigationError';
import { useHistory } from 'react-router-dom';
import initFaro from '@Utils/faro';
import isMaintenanceError from './ErrorPages/isMaintenanceError';
import isAccessDeniedError from './ErrorPages/isAccessDeniedError';

const getErrorPageComponent = (error) => {
  if (isMaintenanceError(error)) return MaintenanceErrorPage;
  if (isAccessDeniedError(error)) return AccessDeniedPage;
  if (error.networkError?.result) return GraphQLErrorPage;
  if (error.networkError) return NetworkErrorPage;
  if (error.graphQLErrors) return ServerErrorPage;
  if (error instanceof NavigationError) return BadNavigationPage;
  if (!navigator.onLine) return OfflineErrorPage;
  return CodeErrorPage;
};

function ErrorFallback({ error, resetErrorBoundary }) {
  const ErrorPageComponent = getErrorPageComponent(error);
  return <ErrorPageComponent onClickReset={resetErrorBoundary} error={error} />;
}

ErrorFallback.propTypes = {
  error: instanceOf(Error).isRequired,
  resetErrorBoundary: func,
};

export default function ErrorHandler({ children }) {
  const history = useHistory();
  const faro = initFaro(history);
  if (faro) {
    return (
      <FaroErrorBoundary
        fallback={(error, resetError) => <ErrorFallback error={error} resetErrorBoundary={resetError} />}
      >
        {children}
      </FaroErrorBoundary>
    );
  }
  return <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>;
}

ErrorHandler.propTypes = {
  children: node,
};
